import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Sale } from '../../../shared/interfaces/sale';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatExpansionModule } from '@angular/material/expansion';
import { CustomerCardComponent } from '../customer-card/customer-card.component';
import { ProductSummaryComponent } from '../product-summary/product-summary.component';
import { BehaviorSubject, catchError, tap, of } from 'rxjs';
import { Step } from '../../../shared/interfaces/step';
import { Summary } from '../../../shared/interfaces/steps/summary';
import { TotalSummaryComponent } from '../total-summary/total-summary.component';
import { FormInputComponent } from '../form-field/form-input.component';
import { ReactiveFormsModule, FormControl, FormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { CouponSummaryComponent } from '../coupon-summary/coupon-summary.component';
import { MatButtonModule } from '@angular/material/button';
import { CouponsService } from '../../../tools/services/coupons.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { AlertsService } from '../../../admin/services/alerts.service';
import { HasValidationComponent } from '../../../admin/components/abstract/has-validation-component';
import { noWhiteSpaceValidator } from '../../../shared/validators/no-white-space-validator';
import { PrizeSummaryComponent } from '../prize-summary/prize-summary.component';

@Component({
    selector: 'app-step-display-summary',
    standalone: true,
    imports: [CommonModule, FormInputComponent, MatInputModule, ReactiveFormsModule, MatPaginatorModule, MatExpansionModule, CustomerCardComponent, ProductSummaryComponent, TotalSummaryComponent, FormInputComponent, CouponSummaryComponent, MatButtonModule, FormsModule, PrizeSummaryComponent],
    templateUrl: './step-display-summary.component.html',
    styleUrl: './step-display-summary.component.scss',
})
export class StepDisplaySummaryComponent extends HasValidationComponent {
    public _step: Step | undefined;

    @Output() public readonly couponApplied = new EventEmitter<boolean>();

    public couponFormControl = new FormControl<string | null>(null, [Validators.required, noWhiteSpaceValidator()]);

    public isApplyingCoupon$ = new BehaviorSubject<boolean>(false);

    public mode: 'simple' | 'detailed' | undefined;

    @Input({ required: true }) public sale!: Sale;

    @Input({ required: true }) public steps!: Step[];

    public constructor(
        private readonly cdr: ChangeDetectorRef,
        private readonly coupons: CouponsService,
        private readonly destroyRef: DestroyRef,
        alerts: AlertsService,
    ) {
        super(alerts);
    }

    @Input({ required: true }) public set step(step: Step) {
        this._step = step;
        this.mode = (this.step as Summary).settings?.type;
        this.cdr.markForCheck();
    }

    public get step(): Step {
        return this._step!;
    }

    public applyCoupon(): void {
        if (this.couponFormControl.invalid) {
            return;
        }

        this.isApplyingCoupon$.next(true);
        this.couponFormControl.disable();

        this.coupons.apply(
            this.sale.workflow.store_id as number,
            this.sale.workflow.id,
            this.sale.id as number,
            this.couponFormControl.value,
        ).pipe(
            tap((result) => {
                this.alerts.success(result.message);
                this.couponFormControl.setValue(null);
                this.couponApplied.emit(true);
            }),
            catchError((err) => {
                this.alerts.error(err.error.message);
                return of(null);
            }),
            finalize(() => {
                this.isApplyingCoupon$.next(false);
                this.couponFormControl.enable();
            }),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe();
    }
}
